import { defineStore } from 'pinia'

import moment from 'moment'
import api from '../../api/api'
import store from '..'
import {
    ActivePromocode,
    CurrentTariff,
    MarketplaceIntegration,
    PreviousTariff,
    ProjectPromocode,
} from '../../types/marketplace'
import { DisableIntegrationParams, DisableSubscriptionParams } from '@api/marketplace'
import { findTariffPromocode } from '@/modules/marketplace/components/billing/activePromocode'

// Один запрос на проект. Иначе параллельные загрузки пишут в стор по очереди,
// и более медленный ответ затирает уже полученный тариф.
const currentTariffRequests = new Map<string, Promise<void>>()

export const useBillingStore = defineStore('billng', {
    state: () => ({
        tariffs: [] as MarketplaceIntegration[],
        tariffsWithDiscount: [] as MarketplaceIntegration[],
        activePromocode: {} as ActivePromocode,
        projectPromocodes: [] as ProjectPromocode[],
        selectedTariffId: null as string | null,
        selectedSubscriptionPeriod: null as string | null,
        currentTariff: {} as {
            [projectId: string]: CurrentTariff | null
        },
        previousTariff: {} as {
            [projectId: string]: PreviousTariff | null
        },
        isTrialActivatedBefore: false as boolean,
        paymentModal: {
            opened: false as boolean,
            renewal: false as boolean,
            allowShorterPeriods: true as boolean,
            showTariffPicker: false as boolean,
        },
        // Контекст для режима покупки доп. канала (add-channel)
        addChannelContext: {
            name: null as string | null,
            icon: null as string | null,
            kind: null as string | null,
            itemKind: null as string | null,
            integrationId: null as string | null,
            subscriptionId: null as number | null,
            renewalMode: null as string | null,
        },
    }),
    actions: {
        async fetchCurrentTariff(projectId: string) {
            if (!projectId) return

            const pending = currentTariffRequests.get(projectId)
            if (pending) return pending

            const request = (async () => {
                const { data, error } = await api.marketplace.getCurrentTariff(projectId)
                if (error) {
                    store.dispatch('callNotify', 'Произошла ошибка при загрузке текущего тарифа')
                    return
                }
                this.currentTariff = Object.assign({}, this.currentTariff, {
                    [projectId]: data?.currentTariff ?? null,
                })
                this.previousTariff = Object.assign({}, this.previousTariff, {
                    [projectId]: data?.previousTariff ?? null,
                })
                this.isTrialActivatedBefore = data?.isTrialActivatedBefore
            })()

            currentTariffRequests.set(projectId, request)
            try {
                await request
            } finally {
                if (currentTariffRequests.get(projectId) === request) {
                    currentTariffRequests.delete(projectId)
                }
            }
        },

        async fetchTariffs(projectId: string) {
            const { data, error } = await api.marketplace.getAllTariffs(projectId)
            if (error) {
                store.dispatch('callNotify', 'Произошла ошибка при загрузке тарифов')
                return
            }
            this.tariffs = data!.tariffs
            this.tariffsWithDiscount = data!.tariffsWithDiscount
            this.activePromocode = data!.activePromocode
            // До нескольких промокодов billing отдавал один projectPromocode.
            this.projectPromocodes =
                data!.projectPromocodes ?? (data!.projectPromocode ? [data!.projectPromocode] : [])
        },
        formatPrice(price: number) {
            return `${new Intl.NumberFormat('ru-RU').format(price)} ₽`
        },
        setPaymentModalOpened(
            value: boolean,
            renewal = false,
            allowShorterPeriods = true,
            showTariffPicker = false
        ) {
            this.paymentModal.opened = value
            this.paymentModal.renewal = renewal
            this.paymentModal.allowShorterPeriods = value ? allowShorterPeriods : true
            this.paymentModal.showTariffPicker = value ? Boolean(showTariffPicker) : false
        },
        setAddChannelContext(
            payload: {
                name: string
                icon: string | null
                kind?: string | null
                itemKind?: string | null
                integrationId?: string | null
                subscriptionId?: number | null
                renewalMode?: string | null
            } | null
        ) {
            if (!payload) {
                this.addChannelContext = {
                    name: null,
                    icon: null,
                    kind: null,
                    itemKind: null,
                    integrationId: null,
                    subscriptionId: null,
                    renewalMode: null,
                }
            } else {
                this.addChannelContext = {
                    name: payload.name,
                    icon: payload.icon ?? null,
                    kind: payload.kind ?? null,
                    itemKind: payload.itemKind ?? null,
                    integrationId: payload.integrationId ?? null,
                    subscriptionId:
                        typeof payload.subscriptionId === 'number' ? payload.subscriptionId : null,
                    renewalMode: payload.renewalMode ?? null,
                }
            }
        },
        async disableSubscription(params: DisableSubscriptionParams) {
            const { data, error } = await api.marketplace.disableSubscription(params)
            if (error) {
                store.dispatch('callNotify', 'Произошла ошибка при отмене подписки')
                return
            }
            store.dispatch('callNotify', 'Подписка успешно отменена')
            await this.fetchCurrentTariff(params.projectId)
        },
        async disableIntegration(params: DisableIntegrationParams) {
            const { data, error } = await api.marketplace.disableIntegration(params)
            if (error) {
                store.dispatch('callNotify', 'Произошла ошибка при отмене подписки')
                return
            }
            store.dispatch('callNotify', 'Подписка успешно отменена')
            await this.fetchCurrentTariff(params.projectId)
        },
    },
    getters: {
        isCurrentTariffFetched(state) {
            return (projectId: string) =>
                Object.prototype.hasOwnProperty.call(state.currentTariff, projectId)
        },
        getCurrentTariff(state) {
            return (projectId: string): CurrentTariff | undefined => {
                const tariff = state.currentTariff[projectId]
                if (!tariff || !Object.keys(tariff).length) return undefined
                return tariff
            }
        },
        getPreviousTariff(state) {
            return (projectId: string): PreviousTariff | null => {
                const tariff = state.previousTariff[projectId]
                if (!tariff) return null
                return tariff
            }
        },
        getTariffPeriodEnd(state) {
            return (projectId: string): string => {
                const current = state.currentTariff[projectId]
                const previous = state.previousTariff[projectId]
                return (
                    current?.endOfUse ||
                    current?.activeUntil ||
                    current?.nextPayment ||
                    previous?.activeUntil ||
                    ''
                )
            }
        },
        getBlockedTariffPayUntil(state) {
            return (projectId: string): string => {
                const current = state.currentTariff[projectId]
                if (current && Object.keys(current).length) {
                    return current.endOfUse || current.activeUntil || current.nextPayment || ''
                }
                const uninstalledAt = state.previousTariff[projectId]?.uninstalledAt
                if (!uninstalledAt) return ''
                const deadline = new Date(uninstalledAt)
                if (Number.isNaN(deadline.getTime())) return ''
                deadline.setDate(deadline.getDate() + 3)
                return deadline.toISOString()
            }
        },
        getTariffsWithDiscount(state) {
            return (projectId: string): CurrentTariff => {
                const tariff = state.currentTariff
                if (!tariff[projectId]) return undefined
                return tariff[projectId]
            }
        },
        getIsTrialActivatedBefore(state) {
            return () => state.isTrialActivatedBefore || false
        },
        getTariffById(state) {
            return (tariffId: string): MarketplaceIntegration | undefined =>
                state.tariffs.find(tariff => tariff.id === tariffId)
        },
        getTariffForPeriodLabels(state) {
            if (state.selectedTariffId) {
                return state.tariffs.find((tariff) => tariff.id === state.selectedTariffId)
            }
            return state.tariffs[0]
        },
        tariffIds: state => state.tariffs.map(el => el.id),
        getCurrentTariffState: state => (projectId: string) => {
            const currentTariff = state.currentTariff[projectId]
            if (!currentTariff || !Object.keys(currentTariff).length) return ''
            if (moment(currentTariff.endOfUse).isBefore(moment())) {
                return `Приостановлен`
            }
            if (currentTariff.endOfUse && currentTariff.isTrial) {
                const formattedDate = new Date(currentTariff.endOfUse).toLocaleDateString()
                return `Активен до ${formattedDate} (пробный период)`
            }
            if (currentTariff.endOfUse) {
                const formattedDate = new Date(currentTariff.endOfUse).toLocaleDateString()
                return `Активен до ${formattedDate}`
            }
        },
        isCurrentTariffActive: state => (projectId: string) => {
            const currentTariff = state.currentTariff[projectId]
            if (!currentTariff || !Object.keys(currentTariff).length) return false
            if (moment(currentTariff.endOfUse).isBefore(moment())) {
                return false
            }
            return true
        },
        getTarrifRegularPrice: state => (tariffId: string, subscriptionPeriod: string | undefined) => {
            if (!subscriptionPeriod) return 0
            const tariff = state.tariffs.find(tariff => tariff.id === tariffId)
            if (!tariff) return 0
            const periodData = tariff.complexPrice?.monthPeriod?.[subscriptionPeriod]
            return periodData?.regularPayment?.sum ?? 0
        },
        getTarrifPromoPrice: state => (tariffId: string, subscriptionPeriod: string | undefined) => {
            if (!subscriptionPeriod) return 0
            const tariff = state.tariffsWithDiscount.find(tariff => tariff.id === tariffId)
            if (!tariff) return 0
            const periodData = tariff.complexPrice?.monthPeriod?.[subscriptionPeriod]
            return periodData?.regularPayment?.sum ?? 0
        },
        // Промокод, который действует на этот тариф; activePromocode — один на все тарифы.
        getTariffPromocode: state => (tariffId: string): ProjectPromocode | null =>
            findTariffPromocode(state.projectPromocodes, tariffId) ?? null,
    },
})
